import { useState } from 'react';
import { Box, Button, Typography, Modal, FormHelperText } from '@mui/material';
import theme from '../LayoutComponents/theme';
import ResumeDisplay from './ResumeDisplay';
import { updateFileRequest, userSkillsRequest } from '../../remote/usersRequests';
import LoadingCircle from '../LayoutComponents/LoadingCircle';

export default function ProcessResumeButton({ session, handleSkillsUpdate }) {
    const [resume, setResume] = useState(null);
    const [resumeModalOpen, setResumeModalOpen] = useState(false);
    const [resumeData, setResumeData] = useState(null);
    const [resumeName, setResumeName] = useState('');
    const [loading, setLoading] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            alert("No file selected or file could not be accessed.");
            return;
        }
        setResume(file);
        setResumeName(file.name);
    };

    const handleSubmitFile = async (event) => {
        event.preventDefault();

        if (!resume || typeof resume.size !== "number" || resume.size === 0) {
            alert("Resume file is not accessible.");
            return;
        }

        setLoading(true);

        try {
            const res = await updateFileRequest(session.token, resume, session?.userId);

            if (res.status === 200) {
                // stop loading before showing modal
                setLoading(false);

                // open modal and display parsed resume data
                setResumeData(res.data);
                setResumeModalOpen(true);

                // fetch updated skills in background
                try {
                    const skillsResponse = await userSkillsRequest(session?.token, session?.userId);
                    handleSkillsUpdate(skillsResponse.data);
                } catch (err) {
                    console.error(err);
                    alert('Error fetching updated skills list.');
                }
            } else {
                alert('Error processing resume.');
                setLoading(false);
            }
        } catch (err) {
            console.error(err);
            alert('Error processing resume.');
            setLoading(false);
        } finally {
            // Reset upload fields but keep modal state intact
            setResume(null);
            setResumeName('');
            const fileInput = document.querySelector('#resume-upload');
            if (fileInput) fileInput.value = '';
        }
    };

    return (
        <Box>
            {/* Resume Modal */}
            <Modal
                open={resumeModalOpen}
                onClose={() => setResumeModalOpen(false)} // user closes manually
                aria-labelledby="resume-modal-title"
                aria-describedby="resume-modal-description"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(2px)',
                }}
            >
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        maxWidth: 600,
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        outline: 'none',
                    }}
                >
                    <ResumeDisplay data={resumeData} />
                </Box>
            </Modal>

            {/* Loading Overlay */}
            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'rgba(255,255,255,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <LoadingCircle />
                    <Typography variant="h5" sx={{ color: theme.colors.purple, textAlign: 'center' }}>
                        Your resume is being processed...
                    </Typography>
                </Box>
            )}

            {/* Upload / Process Section */}
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                {!resume ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <FormHelperText>
                            Ensure all headings in your resume are capitalized for optimal accuracy.
                        </FormHelperText>
                        <input
                            id="resume-upload"
                            type="file"
                            hidden
                            accept=".pdf, .doc, .docx"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="resume-upload">
                            <Button
                                component="span"
                                sx={{
                                    color: 'white',
                                    fontWeight: theme.typography.fontWeightBold,
                                    backgroundColor: theme.colors.purple,
                                    '&:hover': { backgroundColor: theme.colors.purple },
                                }}
                            >
                                Upload Resume
                            </Button>
                        </label>
                    </Box>
                ) : (
                    <Button
                        sx={{
                            color: 'white',
                            fontWeight: theme.typography.fontWeightBold,
                            backgroundColor: theme.colors.purple,
                            '&:hover': { backgroundColor: theme.colors.purple },
                        }}
                        onClick={handleSubmitFile}
                        disabled={loading}
                    >
                        Process Resume
                    </Button>
                )}
            </Box>

            {/* Display selected file name */}
            {resumeName && <Typography variant="body2" sx={{ mt: 1 }}>{resumeName}</Typography>}
        </Box>
    );
}
